import { useState } from 'react';
import { AdminSidebar } from '../../components/shared';
import toast from 'react-hot-toast';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3004/api';

// ── Helpers visuais ──────────────────────────────────────────
function Badge({ method }) {
  const colors = {
    POST:  { bg: '#1a7f5a', color: '#fff' },
    GET:   { bg: '#1565c0', color: '#fff' },
    PUT:   { bg: '#7b5800', color: '#fff' },
    PATCH: { bg: '#5c2d91', color: '#fff' },
  };
  const s = colors[method] || { bg: '#444', color: '#fff' };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontFamily: 'monospace', fontWeight: 700, fontSize: '.72rem',
      padding: '3px 8px', borderRadius: 5, letterSpacing: .5,
    }}>{method}</span>
  );
}

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setOk(true);
    toast.success('Copiado!', { duration: 1200 });
    setTimeout(() => setOk(false), 1800);
  };
  return (
    <button onClick={copy} style={{
      position: 'absolute', top: 10, right: 10,
      background: ok ? '#1a7f5a' : 'rgba(255,255,255,.12)',
      color: '#fff', border: 'none', borderRadius: 6,
      padding: '4px 10px', fontSize: '.72rem', cursor: 'pointer', fontFamily: 'inherit',
      transition: 'background .2s',
    }}>{ok ? '✓ Copiado' : 'Copiar'}</button>
  );
}

function Code({ children, lang = 'json' }) {
  return (
    <div style={{ position: 'relative' }}>
      <pre style={{
        background: '#0d1117', color: '#e6edf3',
        borderRadius: 8, padding: '14px 16px', margin: 0,
        fontSize: '.78rem', lineHeight: 1.6, overflowX: 'auto',
        fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
      }}><code>{children}</code></pre>
      <CopyBtn text={children} />
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 48 }}>
      <h2 style={{
        fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)',
        borderBottom: '2px solid var(--primary)', paddingBottom: 8, marginBottom: 20,
      }}>{title}</h2>
      {children}
    </section>
  );
}

function Endpoint({ method, path, desc, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 10,
      marginBottom: 12, overflow: 'hidden',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', background: 'var(--secondary)',
          border: 'none', padding: '14px 18px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <Badge method={method} />
        <code style={{ fontFamily: 'monospace', fontSize: '.85rem', flex: 1, color: 'var(--text)' }}>
          {path}
        </code>
        <span style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginRight: 8 }}>{desc}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ name, type, required, desc }) {
  return (
    <tr>
      <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '.8rem', color: '#FF6B35' }}>{name}</td>
      <td style={{ padding: '6px 10px', fontSize: '.78rem', color: '#7c85a0' }}>{type}</td>
      <td style={{ padding: '6px 10px' }}>
        {required
          ? <span style={{ background: '#1a7f5a22', color: '#1a7f5a', fontSize: '.7rem', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>obrigatório</span>
          : <span style={{ background: 'var(--secondary)', color: 'var(--text-muted)', fontSize: '.7rem', padding: '1px 6px', borderRadius: 4 }}>opcional</span>}
      </td>
      <td style={{ padding: '6px 10px', fontSize: '.8rem', color: 'var(--text-muted)' }}>{desc}</td>
    </tr>
  );
}

function FieldTable({ children }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
        <thead>
          <tr style={{ background: 'var(--secondary)', textAlign: 'left' }}>
            {['Campo', 'Tipo', 'Req.', 'Descrição'].map(h => (
              <th key={h} style={{ padding: '6px 10px', fontWeight: 600, fontSize: '.78rem', color: 'var(--text-muted)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody style={{ borderTop: '1px solid var(--border)' }}>{children}</tbody>
      </table>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontWeight: 700, fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 6 }}>{children}</div>;
}

// ── Exemplos de código ────────────────────────────────────────
const curlLogin = `curl -X POST ${BASE}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "admin@suaempresa.com",
    "senha": "sua_senha"
  }'`;

const resLogin = `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "usuario": {
    "id": "uuid",
    "nome": "Admin",
    "email": "admin@suaempresa.com",
    "perfil": "admin_empresa",
    "empresa_id": "uuid-da-empresa",
    "slug": "minha-empresa"
  }
}`;

const curlUpsert = `curl -X PUT ${BASE}/erp/produtos/PROD-001 \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer {token}" \\
  -d '{
    "descricao": "Pizza Margherita",
    "categoria_nome": "Pizzas",
    "preco_venda": 45.90,
    "preco_promocional": 39.90,
    "unidade_medida": "UN",
    "status": "ativo"
  }'`;

const resUpsertCriado = `{
  "acao": "criado",
  "produto": {
    "id": "uuid",
    "empresa_id": "uuid",
    "codigo_erp": "PROD-001",
    "descricao": "Pizza Margherita",
    "categoria_id": "uuid",
    "preco_venda": "45.90",
    "preco_promocional": "39.90",
    "unidade_medida": "UN",
    "status": "ativo",
    "destaque": false,
    "ordem": 0,
    "foto_url": null,
    "criado_em": "2024-01-15T10:00:00Z"
  }
}`;

const resUpsertAtualizado = `{
  "acao": "atualizado",
  "produto": { ... }
}`;

const curlListarPendentes = `curl "${BASE}/erp/pedidos/pendentes?limit=50" \\
  -H "Authorization: Bearer {token}"`;

const resListarPendentes = `{
  "dados": [
    {
      "id": "uuid-do-pedido",
      "data_hora": "2024-01-15T14:30:00Z",
      "tipo": "entrega",
      "forma_pagamento": "pix",
      "status": "pendente",
      "valor_total": "85.80",
      "nome_cliente": "João Silva",
      "cpf_cliente": null,
      "telefone": "11999999999",
      "email": "joao@email.com",
      "endereco": "Rua das Flores",
      "numero": "123",
      "bairro": "Centro",
      "complemento": null,
      "cidade": "São Paulo",
      "estado": "SP",
      "cep": "01310-100",
      "observacao": null,
      "criado_em": "2024-01-15T14:30:00Z",
      "itens": [
        {
          "descricao_produto": "Pizza Margherita",
          "codigo_erp": "PROD-001",
          "codigo_ean": null,
          "unidade_medida": "UN",
          "quantidade": "2.000",
          "valor_unitario": "39.90",
          "valor_total": "79.80",
          "observacao": "Sem cebola"
        }
      ]
    }
  ],
  "total": 1
}`;

const curlConfirmarConsumo = `curl -X PATCH "${BASE}/erp/pedidos/{id}/consumir" \\
  -H "Authorization: Bearer {token}"`;

const resConfirmarConsumo = `{
  "acao": "consumido",
  "pedido": {
    "id": "uuid-do-pedido",
    "status": "pendente",
    "erp_consumido": true,
    "erp_consumido_em": "2024-01-15T14:31:00Z"
  }
}`;

const curlListarPedidos = `curl "${BASE}/erp/pedidos?status=pendente&page=1&limit=20" \\
  -H "Authorization: Bearer {token}"`;

const resListarPedidos = `{
  "dados": [
    {
      "id": "uuid-do-pedido",
      "data_hora": "2024-01-15T14:30:00Z",
      "tipo": "entrega",
      "forma_pagamento": "pix",
      "status": "pendente",
      "valor_total": "85.80",
      "nome_cliente": "João Silva",
      "telefone": "11999999999",
      "email": "joao@email.com",
      "endereco": "Rua das Flores",
      "numero": "123",
      "bairro": "Centro",
      "cidade": "São Paulo",
      "estado": "SP",
      "observacao": null,
      "criado_em": "2024-01-15T14:30:00Z"
    }
  ],
  "total": 42,
  "page": 1,
  "limit": 20
}`;

const curlBuscarPedido = `curl "${BASE}/erp/pedidos/{id}" \\
  -H "Authorization: Bearer {token}"`;

const resBuscarPedido = `{
  "id": "uuid-do-pedido",
  "status": "pendente",
  "tipo": "entrega",
  "forma_pagamento": "pix",
  "valor_total": "85.80",
  "nome_cliente": "João Silva",
  "telefone": "11999999999",
  "email": "joao@email.com",
  "endereco": "Rua das Flores",
  "numero": "123",
  "bairro": "Centro",
  "cidade": "São Paulo",
  "estado": "SP",
  "cep": "01310-100",
  "observacao": null,
  "erp_consumido": false,
  "erp_consumido_em": null,
  "criado_em": "2024-01-15T14:30:00Z",
  "itens": [
    {
      "descricao_produto": "Pizza Margherita",
      "codigo_erp": "PROD-001",
      "unidade_medida": "UN",
      "quantidade": 2,
      "valor_unitario": "39.90",
      "valor_total": "79.80",
      "observacao": "Sem cebola"
    }
  ],
  "historico": [
    {
      "status_anterior": null,
      "status_novo": "pendente",
      "observacao": null,
      "data_hora": "2024-01-15T14:30:00Z"
    }
  ]
}`;

const axiosExample = `import axios from 'axios';

const API = axios.create({
  baseURL: '${BASE}',
});

// 1. Autenticar
const { data } = await API.post('/auth/login', {
  email: 'admin@empresa.com',
  senha: 'senha123',
});
const token = data.token;
API.defaults.headers.common['Authorization'] = \`Bearer \${token}\`;

// 2. Sincronizar categorias
await API.put('/erp/categorias/CAT-01', { descricao: 'Pizzas', ordem: 1 });
await API.put('/erp/categorias/CAT-02', { descricao: 'Bebidas', ordem: 2 });

// 3. Sincronizar subcategorias
await API.put('/erp/subcategorias/SUB-01', {
  descricao: 'Pizzas Salgadas',
  categoria_codigo_erp: 'CAT-01',
});

// 4. Sincronizar produto (referenciando categoria e subcategoria pelo codigo_erp)
await API.put('/erp/produtos/PROD-001', {
  descricao: 'Pizza Margherita',
  categoria_codigo_erp: 'CAT-01',
  subcategoria_codigo_erp: 'SUB-01',
  preco_venda: 45.90,
});

// 5. Poll da fila de pedidos ainda não consumidos pelo ERP
const { data } = await API.get('/erp/pedidos/pendentes', {
  params: { limit: 50 },
});

for (const pedido of data.dados) {
  // ... processar pedido.itens no seu sistema (baixa de estoque, NF-e, etc) ...

  // 6. Confirmar consumo (ack) — o pedido some da fila até a próxima
  //    mudança de status (aceito, em_rota, cancelado, etc)
  await API.patch(\`/erp/pedidos/\${pedido.id}/consumir\`);
}`;

const curlUpsertCat = `curl -X PUT ${BASE}/erp/categorias/CAT-01 \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer {token}" \\
  -d '{"descricao": "Pizzas", "ordem": 1}'`;

const resUpsertCat = `// 201 — criado
{ "acao": "criado",     "categoria": { "id": "uuid", "codigo_erp": "CAT-01", "descricao": "Pizzas", "status": "ativo", "ordem": 1 } }

// 200 — atualizado
{ "acao": "atualizado", "categoria": { ... } }`;

const curlUpsertSubcat = `curl -X PUT ${BASE}/erp/subcategorias/SUB-01 \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer {token}" \\
  -d '{"descricao": "Pizzas Salgadas", "categoria_codigo_erp": "CAT-01", "ordem": 1}'`;

const resUpsertSubcat = `// 201 — criado
{ "acao": "criado",     "subcategoria": { "id": "uuid", "codigo_erp": "SUB-01", "descricao": "Pizzas Salgadas", "categoria_id": "uuid" } }

// 200 — atualizado
{ "acao": "atualizado", "subcategoria": { ... } }`;

// ── Componente principal ──────────────────────────────────────
export default function MasterApiDocs() {
  const navItems = [
    { id: 'auth',          label: 'Autenticação' },
    { id: 'categorias',    label: 'Categorias (ERP)' },
    { id: 'produtos',      label: 'Produtos (ERP)' },
    { id: 'pedidos',       label: 'Pedidos (ERP)' },
    { id: 'status',        label: 'Status & Tipos' },
  ];

  return (
    <div className="admin-layout">
      <AdminSidebar perfil="master" />
      <div className="main-content">
        <div className="topbar">
          <h1>Documentação da API — Integração ERP</h1>
        </div>
        <div className="page-content" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

          {/* Sumário lateral */}
          <div style={{
            width: 180, flexShrink: 0, position: 'sticky', top: 20,
            background: 'var(--secondary)', borderRadius: 10, padding: '14px 0',
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontWeight: 700, fontSize: '.78rem', color: 'var(--text-muted)',
              padding: '0 14px 10px', textTransform: 'uppercase', letterSpacing: .5 }}>
              Índice
            </div>
            {navItems.map(n => (
              <a key={n.id} href={`#${n.id}`} style={{
                display: 'block', padding: '7px 14px', fontSize: '.82rem',
                color: 'var(--text-muted)', textDecoration: 'none',
                borderLeft: '2px solid transparent', transition: 'all .15s',
              }}
              onMouseOver={e => { e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderLeftColor = 'var(--primary)'; }}
              onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderLeftColor = 'transparent'; }}>
                {n.label}
              </a>
            ))}
          </div>

          {/* Conteúdo */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Intro */}
            <div style={{
              background: 'linear-gradient(135deg, #1A1A2E 0%, #2a2a45 100%)',
              borderRadius: 12, padding: '24px 28px', marginBottom: 32, color: '#fff',
            }}>
              <h2 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>🔌 API REST — ArautoFood</h2>
              <p style={{ margin: '0 0 16px', color: 'rgba(255,255,255,.6)', fontSize: '.875rem', lineHeight: 1.7 }}>
                Integre seu ERP ao ArautoFood para sincronizar produtos e consumir pedidos em tempo real.
                Toda comunicação é feita via <strong style={{ color: '#fff' }}>HTTP + JSON</strong> com
                autenticação <strong style={{ color: '#fff' }}>JWT Bearer Token</strong>.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 7, padding: '6px 12px', fontSize: '.78rem' }}>
                  <span style={{ color: 'rgba(255,255,255,.5)' }}>Base URL: </span>
                  <code style={{ color: '#FF6B35' }}>{BASE}</code>
                </div>
                <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 7, padding: '6px 12px', fontSize: '.78rem' }}>
                  <span style={{ color: 'rgba(255,255,255,.5)' }}>Formato: </span>
                  <code style={{ color: '#7ee787' }}>application/json</code>
                </div>
                <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 7, padding: '6px 12px', fontSize: '.78rem' }}>
                  <span style={{ color: 'rgba(255,255,255,.5)' }}>Auth: </span>
                  <code style={{ color: '#79c0ff' }}>Bearer Token (JWT)</code>
                </div>
              </div>
            </div>

            {/* AUTENTICAÇÃO */}
            <Section id="auth" title="1. Autenticação">
              <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
                Todas as rotas ERP exigem um token JWT obtido via login com as credenciais do administrador da empresa.
                O token expira em <strong>8 horas</strong> — renove conforme necessário.
              </p>

              <Endpoint method="POST" path="/auth/login" desc="Obtém token de acesso">
                <div>
                  <Label>Headers</Label>
                  <Code>{`Content-Type: application/json`}</Code>
                </div>
                <div>
                  <Label>Body</Label>
                  <FieldTable>
                    <Field name="email" type="string" required desc="E-mail do admin da empresa" />
                    <Field name="senha" type="string" required desc="Senha do admin da empresa" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlLogin}</Code>
                </div>
                <div>
                  <Label>Resposta 200</Label>
                  <Code>{resLogin}</Code>
                </div>
                <div style={{ background: '#fff3cd22', border: '1px solid #ffc10744', borderRadius: 8, padding: '12px 16px', fontSize: '.82rem', color: 'var(--text-muted)' }}>
                  ⚠️ Guarde o valor de <code style={{ color: '#FF6B35' }}>token</code>. Envie-o em todas as chamadas seguintes no header
                  <code style={{ color: '#FF6B35', marginLeft: 4 }}>Authorization: Bearer {'{'}{'{'}token{'}'}{'}'}</code>
                </div>
              </Endpoint>
            </Section>

            {/* CATEGORIAS */}
            <Section id="categorias" title="2. Categorias — Sincronização via ERP">
              <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
                Sincronize categorias e subcategorias antes dos produtos. O <code style={{ color: '#FF6B35' }}>codigo_erp</code> identifica
                unicamente cada categoria na empresa — envios repetidos atualizam sem duplicar.
              </p>

              <Endpoint method="PUT" path="/erp/categorias/:codigo_erp" desc="Cria ou atualiza categoria pelo código ERP">
                <div>
                  <Label>Headers obrigatórios</Label>
                  <Code>{`Content-Type: application/json\nAuthorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Body (JSON)</Label>
                  <FieldTable>
                    <Field name="descricao" type="string" required desc="Nome da categoria (obrigatório na criação)" />
                    <Field name="status"    type="string" desc="'ativo' ou 'inativo' (padrão: ativo)" />
                    <Field name="ordem"     type="number" desc="Ordem de exibição no cardápio (padrão: 0)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlUpsertCat}</Code>
                </div>
                <div>
                  <Label>Resposta</Label>
                  <Code>{resUpsertCat}</Code>
                </div>
              </Endpoint>

              <Endpoint method="PUT" path="/erp/subcategorias/:codigo_erp" desc="Cria ou atualiza subcategoria pelo código ERP">
                <div>
                  <Label>Headers obrigatórios</Label>
                  <Code>{`Content-Type: application/json\nAuthorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Body (JSON)</Label>
                  <FieldTable>
                    <Field name="descricao"           type="string" required desc="Nome da subcategoria (obrigatório na criação)" />
                    <Field name="categoria_id"         type="uuid"   desc="UUID da categoria pai. Use este OU categoria_codigo_erp" />
                    <Field name="categoria_codigo_erp" type="string" desc="Código ERP da categoria pai (alternativa ao categoria_id)" />
                    <Field name="status"              type="string" desc="'ativo' ou 'inativo' (padrão: ativo)" />
                    <Field name="ordem"               type="number" desc="Ordem de exibição (padrão: 0)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlUpsertSubcat}</Code>
                </div>
                <div>
                  <Label>Resposta</Label>
                  <Code>{resUpsertSubcat}</Code>
                </div>
              </Endpoint>
            </Section>

            {/* PRODUTOS */}
            <Section id="produtos" title="3. Produtos — Sincronização via ERP">
              <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
                Endpoint idempotente: se o produto com o <code style={{ color: '#FF6B35' }}>codigo_erp</code> já existir
                na empresa, ele é <strong>atualizado</strong>; caso contrário, é <strong>criado</strong>.
                Apenas os campos enviados são alterados na atualização.
              </p>

              <Endpoint method="PUT" path="/erp/produtos/:codigo_erp" desc="Cria ou atualiza produto pelo código ERP">
                <div>
                  <Label>Headers obrigatórios</Label>
                  <Code>{`Content-Type: application/json\nAuthorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Parâmetro de URL</Label>
                  <FieldTable>
                    <Field name=":codigo_erp" type="string" required desc="Código único do produto no seu ERP (ex: PROD-001, SKU-42)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Body (JSON)</Label>
                  <FieldTable>
                    <Field name="descricao" type="string" required desc="Nome do produto (obrigatório apenas na criação)" />
                    <Field name="categoria_id"          type="uuid"   desc="UUID da categoria. Use este, categoria_codigo_erp ou categoria_nome" />
                    <Field name="categoria_codigo_erp"  type="string" desc="Código ERP da categoria (recomendado na integração ERP)" />
                    <Field name="categoria_nome"        type="string" desc="Nome da categoria (busca case-insensitive). Alternativa aos anteriores" />
                    <Field name="subcategoria_id"       type="uuid"   desc="UUID da subcategoria (opcional)" />
                    <Field name="subcategoria_codigo_erp" type="string" desc="Código ERP da subcategoria (opcional)" />
                    <Field name="preco_venda" type="number" desc="Preço de venda (ex: 45.90)" />
                    <Field name="preco_promocional" type="number" desc="Preço promocional. Envie null ou '' para remover" />
                    <Field name="unidade_medida" type="string" desc="UN · KG · G · L · ML · PCT · CX (padrão: UN)" />
                    <Field name="descricao_detalhada" type="string" desc="Descrição longa / ingredientes" />
                    <Field name="status" type="string" desc="'ativo' ou 'inativo' (padrão: ativo)" />
                    <Field name="destaque" type="boolean" desc="Exibe na seção de destaques (padrão: false)" />
                    <Field name="ordem" type="number" desc="Ordem de exibição (padrão: 0)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlUpsert}</Code>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <Label>Resposta 201 — produto criado</Label>
                    <Code>{resUpsertCriado}</Code>
                  </div>
                  <div>
                    <Label>Resposta 200 — produto atualizado</Label>
                    <Code>{resUpsertAtualizado}</Code>
                  </div>
                </div>
              </Endpoint>
            </Section>

            {/* PEDIDOS */}
            <Section id="pedidos" title="4. Pedidos — Consumo via ERP">
              <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.7 }}>
                Consulte os pedidos da sua empresa com filtros por status e período.
                Cada pedido inclui todos os itens com o <code style={{ color: '#FF6B35' }}>codigo_erp</code> original do produto.
              </p>

              <div style={{ background: '#1565c022', border: '1px solid #1565c044', borderRadius: 8, padding: '12px 16px', fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.7 }}>
                💡 <strong>Fila de consumo (acknowledgment)</strong> — mesmo modelo do iFood: pedidos novos entram
                numa fila de <em>pendentes de consumo</em>. Depois de processar um pedido, confirme o consumo via
                <code style={{ color: '#FF6B35', margin: '0 4px' }}>PATCH /erp/pedidos/:id/consumir</code>
                para removê-lo da fila. Sempre que o status do pedido mudar (aceito, em rota, cancelado etc.),
                ele <strong>volta automaticamente</strong> para a fila de pendentes, para o ERP consumir a atualização.
              </div>

              <Endpoint method="GET" path="/erp/pedidos/pendentes" desc="Fila de pedidos ainda não consumidos pelo ERP">
                <div>
                  <Label>Headers</Label>
                  <Code>{`Authorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Query params</Label>
                  <FieldTable>
                    <Field name="limit" type="number" desc="Máximo de pedidos por chamada, máx 500 (padrão: 100)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlListarPendentes}</Code>
                </div>
                <div>
                  <Label>Resposta 200 (já vem com os itens de cada pedido)</Label>
                  <Code>{resListarPendentes}</Code>
                </div>
              </Endpoint>

              <Endpoint method="PATCH" path="/erp/pedidos/:id/consumir" desc="Confirma o consumo (ack) e remove o pedido da fila">
                <div>
                  <Label>Headers</Label>
                  <Code>{`Authorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Parâmetro de URL</Label>
                  <FieldTable>
                    <Field name=":id" type="uuid" required desc="UUID do pedido (campo 'id' da fila de pendentes)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlConfirmarConsumo}</Code>
                </div>
                <div>
                  <Label>Resposta 200</Label>
                  <Code>{resConfirmarConsumo}</Code>
                </div>
              </Endpoint>

              <Endpoint method="GET" path="/erp/pedidos" desc="Lista pedidos com filtros (histórico geral, sem afetar a fila)">
                <div>
                  <Label>Headers</Label>
                  <Code>{`Authorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Query params</Label>
                  <FieldTable>
                    <Field name="status" type="string" desc="Filtra por status: pendente · aceito · em_rota · entregue · retirado · cancelado" />
                    <Field name="data_ini" type="date" desc="Data início (YYYY-MM-DD)" />
                    <Field name="data_fim" type="date" desc="Data fim (YYYY-MM-DD)" />
                    <Field name="page" type="number" desc="Página (padrão: 1)" />
                    <Field name="limit" type="number" desc="Itens por página, máx 100 (padrão: 20)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlListarPedidos}</Code>
                </div>
                <div>
                  <Label>Resposta 200</Label>
                  <Code>{resListarPedidos}</Code>
                </div>
              </Endpoint>

              <Endpoint method="GET" path="/erp/pedidos/:id" desc="Pedido completo com itens e histórico">
                <div>
                  <Label>Headers</Label>
                  <Code>{`Authorization: Bearer {token}`}</Code>
                </div>
                <div>
                  <Label>Parâmetro de URL</Label>
                  <FieldTable>
                    <Field name=":id" type="uuid" required desc="UUID do pedido (campo 'id' da listagem)" />
                  </FieldTable>
                </div>
                <div>
                  <Label>Exemplo — cURL</Label>
                  <Code lang="bash">{curlBuscarPedido}</Code>
                </div>
                <div>
                  <Label>Resposta 200</Label>
                  <Code>{resBuscarPedido}</Code>
                </div>
              </Endpoint>
            </Section>

            {/* STATUS E TIPOS */}
            <Section id="status" title="5. Referência — Status e Tipos">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>

                <div style={{ background: 'var(--secondary)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: '.85rem' }}>Status do pedido</div>
                  {[
                    ['pendente',  '#f59e0b', 'Aguardando confirmação'],
                    ['aceito',    '#3b82f6', 'Confirmado pela loja'],
                    ['em_rota',   '#f97316', 'Saiu para entrega'],
                    ['entregue',  '#10b981', 'Entregue ao cliente'],
                    ['retirado',  '#10b981', 'Retirado na loja'],
                    ['cancelado', '#ef4444', 'Cancelado'],
                  ].map(([v, c, l]) => (
                    <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, flexShrink: 0 }} />
                      <code style={{ fontSize: '.78rem', color: '#FF6B35', minWidth: 90 }}>{v}</code>
                      <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{l}</span>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'var(--secondary)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: '.85rem' }}>Tipo do pedido</div>
                  {[
                    ['entrega',  'Entrega no endereço do cliente'],
                    ['retirada', 'Retirada no balcão'],
                    ['mesa',     'Consumo na mesa'],
                  ].map(([v, l]) => (
                    <div key={v} style={{ marginBottom: 7 }}>
                      <code style={{ fontSize: '.78rem', color: '#FF6B35' }}>{v}</code>
                      <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginLeft: 8 }}>{l}</span>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'var(--secondary)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: '.85rem' }}>Formas de pagamento</div>
                  {['dinheiro', 'cartao', 'cartao_credito', 'cartao_debito', 'pix', 'vale_refeicao', 'vale_alimentacao', 'online'].map(v => (
                    <div key={v} style={{ marginBottom: 5 }}>
                      <code style={{ fontSize: '.78rem', color: '#FF6B35' }}>{v}</code>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'var(--secondary)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 12, fontSize: '.85rem' }}>Erros comuns</div>
                  {[
                    ['401', 'Token inválido ou expirado'],
                    ['403', 'Sem permissão para este recurso'],
                    ['404', 'Produto / pedido / categoria não encontrado'],
                    ['400', 'Campo obrigatório ausente'],
                    ['500', 'Erro interno — contate o suporte'],
                  ].map(([code, msg]) => (
                    <div key={code} style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
                      <code style={{ fontSize: '.78rem', color: '#ef4444', minWidth: 32 }}>{code}</code>
                      <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* Exemplo em código */}
            <Section id="exemplo" title="6. Exemplo completo — Node.js / Axios">
              <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.7 }}>
                Fluxo típico de integração: autenticar → sincronizar catálogo → polling de pedidos.
              </p>
              <Code lang="js">{axiosExample}</Code>
            </Section>

          </div>
        </div>
      </div>
    </div>
  );
}
